import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LearnMoreLink } from './learn-more-link';
import { getTimezoneI18nString } from './i18n-mapper';
import { getDisclaimerBannerStyle } from './disclaimer-banner-style';

const commonPropTypes = {
  classes: PropTypes.shape({
    disclaimer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

const DisclaimerBannerUnStyledWrapper = ({
  classes,
  i18n,
  instore,
  children, // content for second line
}) => {
  let text = i18n.getString(_TL_('Not all metrics are in real time. Clicks and impressions in the last 1 hour, UET conversions in the last 2 hours, and Non-UET conversions (App Install, Offline Conversions) in the last 5 hours might not be included.'));

  if (instore) {
    const instoreText = i18n.getString(_TL_('Conversions from store visits in the last 73 hours might not be included.'));

    text = `${text} ${instoreText}`;
  }

  return (
    <div className={classNames('disclaimer-banner', classes.disclaimer)}>
      <LearnMoreLink
        linkText={i18n.getString(_TL_('Learn more'))}
        text={text}
        topic="ext54480"
      />
      <br />
      {children}
    </div>
  );
};

DisclaimerBannerUnStyledWrapper.propTypes = {
  ...commonPropTypes,
  children: PropTypes.node.isRequired,
  instore: PropTypes.bool.isRequired,
};

const DisclaimerBannerUnStyled = ({
  classes,
  i18n,
  timezone,
  instore,
}) => {
  const timeZoneStr = typeof timezone === 'string' ? timezone : i18n.getString(getTimezoneI18nString(timezone));
  const mrcAccreditedText = `${i18n.getString(_TL_('Only clicks are MRC accredited. The time zone for the date range is'))} ${timeZoneStr}`;

  return (
    <DisclaimerBannerUnStyledWrapper
      classes={classes}
      i18n={i18n}
      timezone={timezone}
      instore={instore}
    >
      <LearnMoreLink
        linkText={i18n.getString(_TL_('Learn more'))}
        text={mrcAccreditedText}
        topic="ext54483"
      />
    </DisclaimerBannerUnStyledWrapper>
  );
};

DisclaimerBannerUnStyled.propTypes = {
  ...commonPropTypes,
  timezone: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  instore: PropTypes.bool,
};

DisclaimerBannerUnStyled.defaultProps = {
  instore: false,
};

export const DisclaimerBanner = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerUnStyled),
  getDisclaimerBannerStyle
);

const DisclaimerBannerWithInstoreUnStyled = ({
  classes,
  i18n,
  timezone,
}) => (
  <DisclaimerBannerUnStyled classes={classes} i18n={i18n} timezone={timezone} instore />
);

DisclaimerBannerWithInstoreUnStyled.propTypes = {
  ...commonPropTypes,
  timezone: PropTypes.number.isRequired,
};

export const DisclaimerBannerWithInstore = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerWithInstoreUnStyled),
  getDisclaimerBannerStyle
);

const DisclaimerBannerWithoutTimezoneUnStyled = ({
  classes,
  i18n,
  instore,
}) => (
  <DisclaimerBannerUnStyledWrapper classes={classes} i18n={i18n} instore={instore}>
    {i18n.getString(_TL_('Only clicks are MRC accredited.'))}
  </DisclaimerBannerUnStyledWrapper>
);

DisclaimerBannerWithoutTimezoneUnStyled.propTypes = {
  ...commonPropTypes,
  instore: PropTypes.bool,
};

DisclaimerBannerWithoutTimezoneUnStyled.defaultProps = {
  instore: false,
};

export const DisclaimerBannerWithoutTimezone = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerWithoutTimezoneUnStyled),
  getDisclaimerBannerStyle
);

const DisclaimerBannerWithInstoreWithoutTimezoneUnStyled = ({
  classes,
  i18n,
}) => (
  <DisclaimerBannerWithoutTimezoneUnStyled classes={classes} i18n={i18n} instore />
);

DisclaimerBannerWithInstoreWithoutTimezoneUnStyled.propTypes = commonPropTypes;

export const DisclaimerBannerWithInstoreWithoutTimezone = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerWithInstoreWithoutTimezoneUnStyled),
  getDisclaimerBannerStyle
);
